import React, { useState, useEffect, useLayoutEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
  Avatar,
  Typography,
} from '@mui/material'
import {
  ChevronLeft as ChevronLeftIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  NotificationsNone as NotificationsIcon,
  FilterList as FilterListIcon,
  People as PeopleIcon,
  LocationCity as LocationCityIcon,
  AttachMoney as AttachMoneyIcon,
  LocalHospital as LocalHospitalIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  Handshake as HandshakeIcon,
  TrendingUp as TrendingUpIcon,
  Home as HomeIcon,
  RocketLaunch as RocketLaunchIcon,
  Dashboard as DashboardIcon,
  SmartToy as SmartToyIcon,
  Chat as ChatIcon,
  HelpOutline as HelpOutlineIcon,
  Email as EmailIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material'
import { useHandleLogout } from 'components/Layout/Header/helpers'
import Logo from 'static/images/logo.svg'
import { getSuperUser } from 'data/superUser/actions'
import { useIsUser } from 'pages/Identity/Login'
import { fetchGetEmployerUser } from 'src/redux/employerUser'
import { fetchGetCandidate } from 'src/redux/candidate'
import { checkEmpty, imageAddress } from 'data/helpers'

const DRAWER_WIDTH = 210
const COLLAPSED_DRAWER_WIDTH = 64

const NavigationDrawer = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const identity = useSelector(state => state.identity)
  const { candidateId, employerUserId } = identity
  const candidate = useSelector(state => state.candidate)
  const employerUser = useSelector(state => state.employerUser)
  const superUser = useSelector(state => state.superUser)
  const { userId, isCandidate, isEmployerUser, isSuper } = useIsUser()

  useLayoutEffect(() => {
    const width = collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH
    document.documentElement.style.setProperty('--drawer-width', width + 'px')
    return () => document.documentElement.style.setProperty('--drawer-width', '0px')
  }, [collapsed])

  const handleLogout = useHandleLogout(dispatch, navigate)
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleNavigation = path => {
    navigate(path)
    if (isMobile) {
      setMobileOpen(false)
    }
  }

  const toggleDrawer = () => setCollapsed(!collapsed)

  const getProfilePath = () => {
    if (candidateId) return '/users/candidate/profile'
    if (employerUserId) return 'users/employer_user/profile'
    return '/login'
  }

  const getProfileInfo = () => {
    const user = employerUserId ? employerUser : candidate
    return {
      name: user.id ? `${user.firstName} ${user.lastName}` : 'My Profile',
      image: user.profileImage || '',
      path: getProfilePath(),
    }
  }

  useEffect(() => {
    if (isCandidate && isEmpty(candidate)) {
      dispatch(fetchGetCandidate(candidateId))
    } else if (isEmployerUser && isEmpty(employerUser)) {
      dispatch(fetchGetEmployerUser(employerUserId))
    } else if (isSuper && isEmpty(superUser)) {
      dispatch(getSuperUser(userId))
    }
  }, [
    isCandidate,
    isEmployerUser,
    isSuper,
    candidate,
    employerUser,
    superUser,
    candidateId,
    employerUserId,
    userId,
    dispatch,
  ])

  const profileInfo = getProfileInfo()
  const parsedProfileImage = checkEmpty(profileInfo.image) ? {} : profileInfo.image
  const { key, fileName } = parsedProfileImage
  const imgSrc = key ? imageAddress(key, fileName) : null

  const navIconStyles = {
    minWidth: collapsed ? 'unset' : 36,
    color: theme.palette.text.secondary,
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
    },
  }

  const commonListItemStyles = {
    minHeight: 40,
    justifyContent: collapsed ? 'center' : 'initial',
    '& .MuiTypography-root': {
      fontSize: '0.875rem',
    },
  }

  const sectionHeadingStyles = {
    ...commonListItemStyles,
    px: 2.5,
    mx: 1,
    mt: 2,
    '& .MuiTypography-root': {
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
  }

  const sectionItemStyles = {
    ...commonListItemStyles,
    pl: collapsed ? 2.5 : 4,
    pr: 2.5,
    mx: 1,
    '&.Mui-selected': {
      backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  }

  const topLevelItemStyles = {
    ...commonListItemStyles,
    px: 2.5,
    mx: 1,
    '&.Mui-selected': {
      backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  }

  const renderCandidateMenuItems = () => (
    <>
      <ListItem
        button
        onClick={() => handleNavigation('/jobs')}
        selected={location.pathname === '/jobs'}
        sx={{
          ...commonListItemStyles,
          px: 2.5,
          mx: 1,
          backgroundColor:
            location.pathname !== '/jobs' ? `${theme.palette.primary.main}08` : 'transparent',
          borderRadius: 1,
          '&.Mui-selected': {
            backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
          },
          '&:hover': {
            backgroundColor:
              location.pathname !== '/jobs'
                ? `${theme.palette.primary.main}12`
                : theme.palette.action.hover,
          },
          '& .MuiTypography-root': {
            ...commonListItemStyles['& .MuiTypography-root'],
            fontWeight: 500,
            color: theme.palette.text.primary,
          },
        }}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><SearchIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Job Search' />}
      </ListItem>

      {/* Job Management */}
      <ListItem sx={sectionHeadingStyles}>
        {!collapsed && <ListItemText primary='Job Management' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/applications')}
        selected={location.pathname === '/applications'}
        sx={sectionItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><DescriptionIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='My Job Applications' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/users/alerts')}
        selected={location.pathname === '/users/alerts'}
        sx={sectionItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><NotificationsIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='My Alerts' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/users/filters')}
        selected={location.pathname === '/users/filters'}
        sx={sectionItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><FilterListIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='My Saved Filters' />}
      </ListItem>

      {/* Community */}
      <ListItem sx={sectionHeadingStyles}>
        {!collapsed && <ListItemText primary='Community' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/directory')}
        selected={location.pathname === '/directory'}
        sx={sectionItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><PeopleIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Directory' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/housing')}
        selected={location.pathname === '/housing'}
        sx={sectionItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><LocationCityIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Relocation Help' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/salary')}
        selected={location.pathname === '/salary'}
        sx={sectionItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><AttachMoneyIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Salary Transparency' />}
      </ListItem>
    </>
  )

  const renderEmployerMenuItems = () => {
    return (
      <>
        <ListItem
          button
          onClick={() => handleNavigation('/physicians')}
          selected={location.pathname === '/physicians'}
          sx={{
            ...commonListItemStyles,
            px: 2.5,
            mx: 1,
            backgroundColor:
              location.pathname !== '/physicians' ? `${theme.palette.primary.main}08` : 'transparent',
            borderRadius: 1,
            '&.Mui-selected': {
              backgroundColor: collapsed ? 'transparent' : theme.palette.action.selected,
            },
            '&:hover': {
              backgroundColor:
                location.pathname !== '/physicians'
                  ? `${theme.palette.primary.main}12`
                  : theme.palette.action.hover,
            },
            '& .MuiTypography-root': {
              ...commonListItemStyles['& .MuiTypography-root'],
              fontWeight: 500,
              color: theme.palette.text.primary,
            },
          }}
        >
          {collapsed && <ListItemIcon sx={navIconStyles}><LocalHospitalIcon /></ListItemIcon>}
          {!collapsed && <ListItemText primary='Clinicians' />}
        </ListItem>

        {/* Management */}
        <ListItem sx={sectionHeadingStyles}>
          {!collapsed && <ListItemText primary='Management' />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/jobs')}
          selected={location.pathname === '/jobs'}
          sx={sectionItemStyles}
        >
          {collapsed && <ListItemIcon sx={navIconStyles}><WorkIcon /></ListItemIcon>}
          {!collapsed && <ListItemText primary={'My Jobs'} />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/admin/users')}
          selected={location.pathname === '/admin/users'}
          sx={sectionItemStyles}
        >
          {collapsed && <ListItemIcon sx={navIconStyles}><PeopleIcon /></ListItemIcon>}
          {!collapsed && <ListItemText primary='Users' />}
        </ListItem>

        {/* Preferences */}
        <ListItem sx={sectionHeadingStyles}>
          {!collapsed && <ListItemText primary='Preferences' />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/users/alerts')}
          selected={location.pathname === '/users/alerts'}
          sx={sectionItemStyles}
        >
          {collapsed && <ListItemIcon sx={navIconStyles}><NotificationsIcon /></ListItemIcon>}
          {!collapsed && <ListItemText primary='Alerts' />}
        </ListItem>

        <ListItem
          button
          onClick={() => handleNavigation('/users/filters')}
          selected={location.pathname === '/users/filters'}
          sx={sectionItemStyles}
        >
          {collapsed && <ListItemIcon sx={navIconStyles}><FilterListIcon /></ListItemIcon>}
          {!collapsed && <ListItemText primary='Filters' />}
        </ListItem>

      </>
    )
  }

  const renderAdminMenuItems = () => (
    <>
      <ListItem
        button
        onClick={() => handleNavigation('/super/employers')}
        selected={location.pathname === '/super/employers'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><BusinessIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Employers' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/employer_users')}
        selected={location.pathname === '/super/employer_users'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><BadgeIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Employer Users' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/jobs')}
        selected={location.pathname === '/super/jobs'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><WorkIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Jobs' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/candidates')}
        selected={location.pathname === '/super/candidates'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><PeopleIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Candidates' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/partner_interest')}
        selected={location.pathname === '/super/partner_interest'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><HandshakeIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Partner Interest' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/job_interest')}
        selected={location.pathname === '/super/job_interest'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><TrendingUpIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Job Interest' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/housing')}
        selected={location.pathname === '/super/housing'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><HomeIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Housing' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/background_jobs')}
        selected={location.pathname === '/super/background_jobs'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><RocketLaunchIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Background Job Launcher' />}
      </ListItem>

      <ListItem
        button
        onClick={() => window.open('/hangfire', '_blank')}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><DashboardIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Hangfire Dashboard' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/chatbot_dashboard')}
        selected={location.pathname === '/super/chatbot_dashboard'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><SmartToyIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Chatbot Dashboard' />}
      </ListItem>

      <ListItem
        button
        onClick={() => handleNavigation('/super/chatbot_conversations')}
        selected={location.pathname === '/super/chatbot_conversations'}
        sx={topLevelItemStyles}
      >
        {collapsed && <ListItemIcon sx={navIconStyles}><ChatIcon /></ListItemIcon>}
        {!collapsed && <ListItemText primary='Chatbot Conversations' />}
      </ListItem>
    </>
  )

  const drawerContent = (
    <>
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: collapsed ? 'center' : 'flex-end',
            px: 1,
            pt: 1,
          }}
        >
          <IconButton
            onClick={toggleDrawer}
            size="small"
            sx={{
              padding: 0.5,
              color: theme.palette.grey[400],
              '&:hover': {
                color: theme.palette.text.secondary,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ChevronLeftIcon
              sx={{
                transform: collapsed ? 'rotate(180deg)' : 'none',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
                fontSize: 18,
              }}
            />
          </IconButton>
        </Box>
      )}

      {!isSuper && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            px: collapsed ? 1 : 2,
            pt: isMobile ? 1 : 0,
            pb: 1,
            gap: 1,
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
            onClick={() => handleNavigation(profileInfo.path)}
            src={imgSrc}
          >
            {!imgSrc && <PersonIcon />}
          </Avatar>
          {!collapsed && (
            <ListItemText
              primary={profileInfo.name}
              secondary='View Profile'
              sx={{
                cursor: 'pointer',
                my: 0,
                '& .MuiTypography-body1': {
                  fontSize: '1rem',
                  fontWeight: 500,
                  lineHeight: 1.3,
                },
                '& .MuiTypography-body2': {
                  fontSize: '0.75rem',
                  color: theme.palette.primary.dark,
                },
              }}
              onClick={() => handleNavigation(profileInfo.path)}
            />
          )}
        </Box>
      )}

      {!collapsed && (
        <Divider
          sx={{
            borderColor: theme.palette.divider,
            borderWidth: '1px',
            opacity: 0.2,
          }}
        />
      )}

      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <List>
          {isSuper
            ? renderAdminMenuItems()
            : isEmployerUser
              ? renderEmployerMenuItems()
              : renderCandidateMenuItems()}
        </List>
        <ListItem
          button
          onClick={() => handleNavigation('/support')}
          selected={location.pathname === '/support'}
          sx={{
            ...topLevelItemStyles,
            mt: 1,
          }}
        >
          {collapsed && <ListItemIcon sx={navIconStyles}><HelpOutlineIcon /></ListItemIcon>}
          {!collapsed && <ListItemText primary='Support' />}
        </ListItem>
        <ListItem
          button
          onClick={() => handleNavigation('/contact')}
          selected={location.pathname === '/contact'}
          sx={topLevelItemStyles}
        >
          {collapsed && <ListItemIcon sx={navIconStyles}><EmailIcon /></ListItemIcon>}
          {!collapsed && <ListItemText primary='Contact' />}
        </ListItem>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <Divider
          sx={{
            borderColor: theme.palette.divider,
            borderWidth: '1px',
            opacity: 0.2,
          }}
        />
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            ...commonListItemStyles,
            px: 2.5,
            mx: 1,
            mt: 1,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {collapsed && <ListItemIcon sx={{ ...navIconStyles, color: theme.palette.error.main }}>
            <LogoutIcon />
          </ListItemIcon>}
          {!collapsed && (
            <Typography
              sx={{
                color: theme.palette.error.main,
                fontWeight: 500,
              }}
            >
              Logout
            </Typography>
          )}
        </ListItem>
      </Box>
    </>
  )

  return (
    <>
      {/* Mobile Top Bar with Menu Button */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '56px',
            backgroundColor: theme.palette.background.paper,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px',
            zIndex: theme.zIndex.modal + 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <IconButton
            color="primary"
            aria-label="open navigation"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="img"
            src={Logo}
            alt="MyStethi"
            sx={{
              height: 28,
              width: 28,
              cursor: 'pointer',
            }}
            onClick={() => handleNavigation('/')}
          />
          <Typography
            variant="h6"
            sx={{
              ml: 1,
              color: 'text.primary',
              fontWeight: 500,
              fontSize: '1.1rem'
            }}
          >
            MyStethi
          </Typography>
        </Box>
      )}

      <Box
        component='nav'
        sx={{
          width: { sm: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH },
          flexShrink: { sm: 0 },
          position: 'fixed',
          top: { xs: 0, sm: 'calc(var(--app-header-height, 0px) + var(--filter-bar-height, 0px))' },
          height: { xs: '100%', sm: 'calc(100% - var(--app-header-height, 0px) - var(--filter-bar-height, 0px))' },
          zIndex: theme.zIndex.drawer,
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            zIndex: theme.zIndex.modal + 2,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              display: 'flex',
              flexDirection: 'column',
              top: 0,
              height: '100%',
            },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: 'none', sm: 'block' },
            height: '100%',
            '& .MuiDrawer-paper': {
              position: 'relative',
              width: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid rgba(0, 0, 0, 0.12)',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
              height: '100%',
              backgroundColor: theme.palette.background.paper,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      {/* Add a spacer to push main content to the right of the drawer */}
      <Box
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: { sm: collapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH },
          flexShrink: 0,
        }}
      />
    </>
  )
}

export default NavigationDrawer
